import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import { nativeFetch } from '../utils/nativeApi';

function AskAIPage() {
  const { t } = useTranslation('ask');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || loading) return;

    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setInput('');
    setLoading(true);

    try {
      const response = await nativeFetch('/api/query', {
        method: 'POST',
        body: JSON.stringify({ question }),
      });

      if (response.ok) {
        const data = await response.json();
        const answer = (data.answer && data.answer.trim()) || t('error');
        setMessages(prev => [...prev, { role: 'assistant', content: answer }]);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: errorData.error || t('error'),
          isError: true
        }]);
      }
    } catch (error) {
      console.error('Query error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t('error'),
        isError: true
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-full" style={{ height: 'calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom) - 152px)' }}>
        {/* Header */}
        <div className="px-4 pt-4 pb-2">
          <h2 className="text-2xl font-bold">{t('title')}</h2>
          <p className="text-sm text-black/50 mt-1">{t('subtitle')}</p>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center text-black/40">
              <span className="text-5xl mb-4">🤖</span>
              <p className="text-lg font-bold">{t('emptyTitle')}</p>
              <p className="text-sm mt-2 max-w-xs">{t('emptyHint')}</p>
              <div className="mt-6 space-y-2 w-full max-w-xs">
                {[t('example1'), t('example2'), t('example3')].map((example, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setInput(example);
                      inputRef.current?.focus();
                    }}
                    className="w-full text-start px-3 py-2 text-sm bg-white border-2 border-black/10 rounded-lg hover:border-black/30 hover:bg-gray-50 transition-all text-black/60"
                  >
                    "{example}"
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex mb-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-3 rounded-2xl border-2 border-black ${
                  msg.role === 'user'
                    ? 'bg-accent text-black rounded-br-sm'
                    : msg.isError
                      ? 'bg-red-100 text-red-800 rounded-bl-sm'
                      : 'bg-white text-black rounded-bl-sm'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start mb-3">
              <div className="max-w-[80%] px-4 py-3 rounded-2xl border-2 border-black bg-white rounded-bl-sm">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 bg-black/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-black/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-black/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="px-4 py-3 border-t-2 border-black bg-background-light">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('placeholder')}
              disabled={loading}
              maxLength={1024}
              className="flex-1 px-4 py-3 border-2 border-black rounded-xl font-bold text-sm bg-white shadow-hard-sm focus:outline-none focus:shadow-none focus:translate-x-0.5 focus:translate-y-0.5 transition-all disabled:opacity-50"
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="px-4 py-3 bg-accent border-2 border-black rounded-xl font-bold text-sm shadow-hard-sm hover:shadow-none hover:translate-x-0.5 hover:translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('send')}
            </button>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AskAIPage;
